<template>
  <div class="login-wrapper">
    <!-- Terms Modal -->
    <TermsModal 
      v-if="showTermsModal" 
      :showModal="showTermsModal"
      @terms-accepted="onTermsAccepted"
      @terms-rejected="onTermsRejected"
    />

    <!-- Rest of your existing login template -->
    <div class="image-section">
      <!-- ECG Background Animation -->
      <div class="ecg-background">
        <div class="ecg-container">
          <svg class="ecg-svg" viewBox="0 0 1600 120" preserveAspectRatio="none">
            <defs>
              <g id="heartbeat">
                <path class="ecg-trace" d="M 0 60 L 80 60 L 100 55 L 120 70 L 140 20 L 160 100 L 180 50 L 220 60 L 300 60 L 400 60" />
              </g>
            </defs>
            <g class="ecg-pattern">
              <use href="#heartbeat" x="0"/>
              <use href="#heartbeat" x="400"/>
              <use href="#heartbeat" x="800"/>
              <use href="#heartbeat" x="1200"/>
              <use href="#heartbeat" x="1600"/>
            </g>
          </svg>
        </div>
      </div>

      <!-- Medical Bubbles -->
      <div class="medical-bubbles">
        <div class="bubble"></div>
        <div class="bubble"></div>
        <div class="bubble"></div>
      </div>

      <!-- Image Content -->
      <div class="image-content">
        <h1 class="hero-title">ProxyMed Clinical Decision Engine</h1>
        <p class="hero-subtitle">ProxyMed turns complex patient data into fully validated, actionable care pathways.</p>
        <div class="hero-features">
          <div class="feature-item">
            <div class="feature-icon">✓</div>
            <span>Accurate</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">✓</div>
            <span>Validated</span>
          </div>
          <div class="feature-item">
            <div class="feature-icon">✓</div>
            <span>Actionable</span>
          </div>
        </div>
      </div>
    </div>

    <div class="form-section">
      <div class="login-container">
        <div class="login-header">
  <div class="logo">
    <img src="../assets/ProxyMed_Logo_RGB.svg" alt="ProxyMed">
  </div>
  <h1 class="login-title">{{ isResetMode ? 'Reset Password' : 'Welcome back' }}</h1>
  
  <!-- Error Message in place of subtitle -->
  <div v-if="loginError" class="login-error">
    {{ loginError }}
  </div>
  <p v-else class="login-subtitle">
    {{ isResetMode ? 'Enter your email to receive reset instructions' : 'Sign in to your account' }}
  </p>
</div>

        <!-- Success Message -->
        <div v-if="showSuccess" class="success-message show">
          Password reset instructions have been sent to <strong>{{ successEmail }}</strong>.
        </div>

        <!-- Login Form -->
        <form v-if="!isResetMode" class="login-form" @submit.prevent="handleSignIn">
          <div class="form-group">
            <label for="email" class="form-label">Email</label>
            <input 
              type="text" 
              id="email" 
              v-model="email"
              class="form-input" 
              placeholder="Enter your email"
              required
            >
          </div>

          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <div class="password-group">
              <input 
                :type="showPassword ? 'text' : 'password'"
                id="password" 
                v-model="password"
                class="form-input" 
                placeholder="Enter your password"
                required
              >
              <button type="button" class="password-toggle" @click="togglePassword">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="!showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path v-if="!showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  <path v-if="showPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="form-options">
            <div class="remember-me">
              <div 
                class="toggle-switch" 
                :class="{ 'active': rememberMe }"
                @click="rememberMe = !rememberMe"
                tabindex="0"
                @keydown="handleToggleKeydown"
              >
                <div class="toggle-slider"></div>
              </div>
              <span class="remember-label">Remember me</span>
            </div>
            <a href="#" class="forgot-password" @click.prevent="showResetForm">Forgot password?</a>
          </div>

          <button 
            type="submit" 
            class="sign-in-btn" 
            :class="{ 'loading': isLoading }"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="loading-spinner"></span>
            {{ isLoading ? 'Signing in...' : 'Sign In' }}
          </button>
        </form>

        <!-- Forgot Password Form -->
        <form v-if="isResetMode" class="login-form" @submit.prevent="handleForgotPassword">
          <div class="form-group">
            <label for="resetEmail" class="form-label">Email Address</label>
            <input 
              type="email" 
              id="resetEmail" 
              v-model="resetEmail"
              class="form-input" 
              placeholder="Enter your email address"
              required
            >
          </div>

          <button 
            type="submit" 
            class="sign-in-btn"
            :class="{ 'loading': isResetLoading }"
            :disabled="isResetLoading"
          >
            <span v-if="isResetLoading" class="loading-spinner"></span>
            {{ isResetLoading ? 'Sending...' : 'Send Reset Link' }}
          </button>

          <button type="button" class="back-btn" @click="showLoginForm">
            ← Back to Sign In
          </button>
        </form>

        <div class="login-footer">
          <p class="signup-link">
            Don't have an account? <a href="#" @click.prevent="goToSignup">Sign up for free</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { LoginModel } from "@/entities/LoginModel";
import { HttpHandler } from "@/http/HttpHandler";
import { toastService } from "@/services/toastService";
import TermsModal from "@/components/TermsModal.vue";
import { analytics } from '@/services/analytics'; // ← GA4 Analytics




@Component({
  components: {
    TermsModal
  }
})
export default class Login extends Vue {
  email: string = "";
  password: string = "";
  resetEmail: string = "";
  successEmail: string = "";
  rememberMe: boolean = false;
  showPassword: boolean = false;
  isLoading: boolean = false;
  isResetLoading: boolean = false;
  isResetMode: boolean = false;
  showSuccess: boolean = false;
  returnUrl: string | (string | null)[] = "";
  showTermsModal: boolean = false;
  loginError: string = "";

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  showResetForm() {
    this.isResetMode = true;
    this.showSuccess = false;
    
    // Track forgot password click
    analytics.trackEvent('forgot_password_clicked', {
      event_category: 'Authentication'
    });
    
    this.$nextTick(() => {
      const resetEmailInput = document.getElementById('resetEmail');
      if (resetEmailInput) {
        resetEmailInput.focus();
      }
    });
  }

  showLoginForm() {
    this.isResetMode = false;
    this.showSuccess = false;
    this.resetEmail = "";
    this.$nextTick(() => {
      const emailInput = document.getElementById('email');
      if (emailInput) {
        emailInput.focus();
      }
    });
  }

  handleToggleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.rememberMe = !this.rememberMe;
    }
  }

  goToSignup() {
    // Track signup link click
    analytics.trackEvent('signup_link_clicked', {
      event_category: 'Navigation',
      source: 'login_page'
    });
    
    this.$router.push('/signup');
  }

  validateForm(): boolean {
    if (!this.email.trim()) {
      toastService.showError('Please enter your email address to continue.');
      return false;
    }
    
    if (!this.password.trim()) {
      toastService.showError('Please enter your password to sign in.');
      return false;
    }
    
    if (this.password.length < 6) {
      toastService.showError('Your password must be at least 6 characters long.');
      return false;
    }
    
    return true;
  }

  validateResetEmail(): boolean {
    if (!this.resetEmail.trim()) {
      toastService.showError('Please enter your email address to receive reset instructions.');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.resetEmail.trim())) {
      toastService.showError('Please enter a valid email address (e.g., doctor@hospital.com).');
      return false;
    }
    
    return true;
  }

  hasAcceptedTerms(): boolean {
    const accepted = localStorage.getItem('proxymed_terms_accepted');
    return accepted === 'true';
  }

  async handleSignIn() {
  if (!this.validateForm()) {
    return;
  }

  this.isLoading = true;
  this.loginError = ""; // Clear any previous errors
  
  try {
    const loginForm = new LoginModel(
      this.email,
      this.password,
      this.rememberMe,
      this.returnUrl
    );

    await this.$store.dispatch("authentication/login", loginForm);

    // GA4 TRACKING
    const account = this.$store.getters['authentication/account'];
    
    if (account) {
      console.log('🔍 Account object:', account);
      console.log('🔍 fillSurvey value:', account.fillSurvey);
      
      if (account.fillSurvey !== undefined && account.fillSurvey !== null) {
        localStorage.setItem('fillSurvey', JSON.stringify(account.fillSurvey));
        console.log('✅ fillSurvey saved to localStorage:', account.fillSurvey);
      } else {
        console.warn('⚠️ fillSurvey is undefined or null in account object');
      }
      
      analytics.identifyUser({
        id: account.id || account.userId || this.email,
        email: account.email || this.email,
        username: account.username || account.name || this.email.split('@')[0],
        role: account.role || 'Unknown',
        organization: account.organization || 'Unknown',
        createdAt: account.createdAt || new Date().toISOString(),
        plan: account.plan || 'Free'
      });
      
      analytics.trackEvent('login', {
        method: 'email',
        user_role: account.role || 'Unknown',
        remember_me: this.rememberMe
      });
      
      console.log('✅ Login tracked for:', account.email || this.email);
    } else {
      console.warn('⚠️ Account object is null or undefined');
      
      analytics.identifyUser({
        id: this.email,
        email: this.email,
        role: 'Unknown',
        organization: 'Unknown',
        createdAt: new Date().toISOString(),
        plan: 'Free'
      });
      
      analytics.trackEvent('login', {
        method: 'email',
        remember_me: this.rememberMe
      });
    }

    if (this.hasAcceptedTerms()) {
      this.$store.dispatch("authentication/navigateAfterTermsAccepted", {
        returnUrl: this.returnUrl
      });
    } else {
      this.showTermsModal = true;
    }
  } catch (error: any) {
    console.error('Login error:', error);
    
    let errorMessage = "Unable to sign in. Please check your credentials and try again.";
    
    if (error?.response?.status) {
      switch (error.response.status) {
        case 401:
          errorMessage = "Invalid email or password. Please try again.";
          break;
        case 404:
          errorMessage = "Account not found. Please check your email address.";
          break;
        case 422:
          errorMessage = error?.response?.data?.message || "Please enter valid credentials.";
          break;
        case 429:
          errorMessage = "Too many login attempts. Please wait before trying again.";
          break;
        case 500:
          errorMessage = "Server error. Please try again in a moment.";
          break;
        default:
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
      }
    } else if (error?.message) {
      if (error.message.includes('Network Error')) {
        errorMessage = "Network connection failed. Please check your internet.";
      } else {
        errorMessage = error.message;
      }
    }
    
    this.loginError = errorMessage; // ← Set error message
    
    // Track login error
    analytics.trackError(
      errorMessage,
      'login_error',
      'Login'
    );
    
    // Clear password field on error
    this.password = "";
  } finally {
    this.isLoading = false;
  }
}

  onTermsAccepted() {
    toastService.showSuccess('Terms accepted. Welcome to ProxyMed!');
    
    // Track terms acceptance
    analytics.trackEvent('terms_accepted', {
      event_category: 'Authentication'
    });
    
    this.$store.dispatch("authentication/navigateAfterTermsAccepted", {
      returnUrl: this.returnUrl
    });
  }

  onTermsRejected() {
    toastService.showError('You must accept the terms to use ProxyMed.');
    
    // Track terms rejection
    analytics.trackEvent('terms_rejected', {
      event_category: 'Authentication'
    });
    
    this.$store.dispatch("authentication/logout").then(() => {
      this.email = "";
      this.password = "";
      this.rememberMe = false;
    }).catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        console.error('Logout error:', error);
      }
      this.email = "";
      this.password = "";
      this.rememberMe = false;
    });
  }

  async handleForgotPassword() {
    if (!this.validateResetEmail()) {
      return;
    }
    
    this.isResetLoading = true;
    try {
      const httpHandler = new HttpHandler(this.$store);
      const response = await httpHandler.forgotPassword(this.resetEmail);
      
      this.showSuccess = true;
      this.successEmail = this.resetEmail;
      
      // ==========================================
      // GA4 TRACKING - PASSWORD RESET REQUEST
      // ==========================================
      analytics.trackPasswordReset();
      analytics.trackEvent('password_reset_requested', {
        email: this.resetEmail,
        event_category: 'Authentication'
      });
      // ==========================================
      
      const apiMessage = response?.message || "Password reset instructions have been sent.";
      toastService.showSuccess(apiMessage);
      
      this.resetEmail = "";
      
      setTimeout(() => {
        this.showLoginForm();
      }, 5000);
      
    } catch (error: any) {
      console.error("Password reset failed:", error);
      
      let errorMessage = "Unable to send reset email. Please try again.";
      
      if (error?.response?.status) {
        switch (error.response.status) {
          case 404:
            errorMessage = error?.response?.data?.message || "We couldn't find an account with that email address. Please check the email and try again.";
            break;
          case 429:
            errorMessage = error?.response?.data?.message || "You've requested too many password resets. Please wait 10 minutes before trying again.";
            break;
          case 422:
            errorMessage = error?.response?.data?.message || "Please enter a valid email address.";
            break;
          case 500:
            errorMessage = error?.response?.data?.message || "Our email service is temporarily unavailable. Please try again in a few minutes.";
            break;
          default:
            if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            }
        }
      } else if (error?.message) {
        if (error.message.includes('Network Error')) {
          errorMessage = "Network connection failed. Please check your internet connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }
      
      // ==========================================
      // GA4 TRACKING - PASSWORD RESET ERROR
      // ==========================================
      analytics.trackError(
        errorMessage,
        'password_reset_error',
        'ForgotPassword'
      );
      // ==========================================
      
      toastService.showError(errorMessage);
    } finally {
      this.isResetLoading = false;
    }
  }

  created() {
    this.returnUrl = this.$route.query.returnUrl;
  }

  mounted() {
    this.$nextTick(() => {
      const emailInput = document.getElementById('email');
      if (emailInput) {
        emailInput.focus();
      }
    });
  }
}
</script>




<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

/* Reset & Base Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  height: 100vh;
  overflow: hidden;
  display: flex;
}

/* Toast Container - Fixed at top-right */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
}

.toast-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 320px;
  max-width: 400px;
  pointer-events: auto;
  transform: translateX(400px);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.toast-message.toast-show {
  transform: translateX(0);
  opacity: 1;
}

.toast-message.toast-hide {
  transform: translateX(400px);
  opacity: 0;
  transition: all 0.3s ease-in;
}

.toast-message.success {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.95), rgba(22, 163, 74, 0.9));
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
}

.toast-message.error {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.9));
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
}

.toast-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 4px;
  line-height: 1.2;
}

.toast-text {
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  opacity: 0.95;
  word-wrap: break-word;
}

.toast-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: currentColor;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.toast-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.toast-close:active {
  transform: scale(0.95);
}

/* Main Container */
.login-wrapper {
  display: flex;
  width: 100%;
  height: 100vh;
}

/* Left Side - Image Section (75%) */
.image-section {
  flex: 0 0 75%;
  position: relative;
  background: linear-gradient(135deg, 
      rgba(47, 69, 165, 0.8), 
      rgba(102, 123, 242, 0.7)),
     //    url('../../src/assets/Proxy-medbackground.png')
     url('../../src/assets/Proxy-medbackground.png') 
     center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* ECG Background Animation */
.ecg-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

.ecg-container {
  position: absolute;
  top: 40%;
  left: 0;
  width: 100%;
  height: 120px;
  opacity: 0.3;
}

.ecg-svg {
  width: 100%;
  height: 100%;
}

.ecg-pattern {
  animation: ecgScroll 8s linear infinite;
  will-change: transform;
}

.ecg-trace {
  stroke: #ffffff;
  stroke-width: 2;
  fill: none;
  filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.8));
}

@keyframes ecgScroll {
  from { transform: translateX(0); }
  to { transform: translateX(-400px); }
}

/* Medical Bubbles */
.medical-bubbles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
  overflow: hidden;
}

.bubble {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03));
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  animation: floatBubble 25s ease-in-out infinite;
}

.bubble:nth-child(1) {
  width: 80px;
  height: 80px;
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}

.bubble:nth-child(2) {
  width: 60px;
  height: 60px;
  top: 75%;
  left: 85%;
  animation-delay: 10s;
}

.bubble:nth-child(3) {
  width: 100px;
  height: 100px;
  top: 45%;
  left: 5%;
  animation-delay: 20s;
}

@keyframes floatBubble {
  0%, 100% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0.2;
  }
  25% {
    transform: translateY(-30px) translateX(20px) rotate(90deg);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-60px) translateX(-10px) rotate(180deg);
    opacity: 0.3;
  }
  75% {
    transform: translateY(-40px) translateX(30px) rotate(270deg);
    opacity: 0.5;
  }
}

/* Content overlay on image */
.image-content {
  position: relative;
  z-index: 10;
  text-align: center;
  max-width: 600px;
  padding: 2rem;
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 900;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.hero-subtitle {
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.hero-features {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
}

.feature-icon {
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

/* Right Side - Form Section (25%) */
.form-section {
  flex: 0 0 25%;
  background: linear-gradient(135deg, #f8fafc, #f1f5ff);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
}

.login-container {
  width: 100%;
  max-width: 400px;
  animation: slideLeft 0.8s ease-out;
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Logo Section */
.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo {
  margin-bottom: 1rem;
}

.logo img {
 // height: 40px;
  width: auto;
}

.login-title {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #2f45a5 50%, #667bf2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.login-subtitle {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

/* Form Styles */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  position: relative;
}

.form-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 10px;
  font-size: 0.95rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  color: #1e293b;
}

.form-input:focus {
  outline: none;
  border-color: #667bf2;
  box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.1);
  background: rgba(255, 255, 255, 0.95);
}

.form-input::placeholder {
  color: #94a3b8;
}

/* Password Input with Icon */
.password-group {
  position: relative;
}


.password-toggle {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.3s ease;
}

.password-toggle:hover {
  color: #2f45a5;
}

/* Remember Me & Forgot Password */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.25rem 0;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.toggle-switch {
  position: relative;
  width: 38px;
  height: 20px;
  background: #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.toggle-switch.active {
  background: #667bf2;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-slider {
  transform: translateX(18px);
}

.remember-label {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
}

.forgot-password {
  color: #667bf2;
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 600;
  transition: color 0.3s ease;
  cursor: pointer;
}

.forgot-password:hover {
  color: #2f45a5;
  text-decoration: underline;
}

/* Sign In Button */
.sign-in-btn {
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(47, 69, 165, 0.25);
  margin-top: 0.75rem;
}

.sign-in-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(47, 69, 165, 0.35);
}

.sign-in-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Loading State */
.sign-in-btn.loading {
  pointer-events: none;
  opacity: 0.8;
}

.loading-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Back to Login Button */
.back-btn {
  width: 100%;
  padding: 0.875rem;
  background: transparent;
  color: #667bf2;
  border: 2px solid rgba(102, 123, 242, 0.3);
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.75rem;
}

.back-btn:hover {
  background: rgba(102, 123, 242, 0.05);
  border-color: #667bf2;
  transform: translateY(-1px);
}

/* Success Message */
.success-message {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #16a34a;
  padding: 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 1rem;
  text-align: center;
}

.success-message.show {
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Footer */
.login-footer {
  text-align: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
}

.signup-link {
  color: #64748b;
  font-size: 0.8rem;
}

.signup-link a {
  color: #667bf2;
  text-decoration: none;
  font-weight: 600;
}

.signup-link a:hover {
  text-decoration: underline;
}

/* Focus indicators for accessibility */
.toggle-switch:focus-visible {
  outline: 2px solid #667bf2;
  outline-offset: 2px;
}

.sign-in-btn:focus-visible {
  outline: 2px solid #667bf2;
  outline-offset: 2px;
}


.login-error {
  color: #ef4444;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  margin-top: 0.5rem;
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.login-subtitle {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .image-section {
    flex: 0 0 60%;
  }
  
  .form-section {
    flex: 0 0 40%;
  }

  .hero-title {
    font-size: 2.5rem;
  }
}

@media (max-width: 768px) {
  .login-wrapper {
    flex-direction: column;
  }

  .image-section {
    display: none;
  }

  .form-section {
    flex: 1;
    padding: 1.5rem;
    justify-content: center;
  }

  .login-container {
    max-width: 400px;
    width: 100%;
  }

  .login-header {
    margin-bottom: 1.25rem;
  }

  .logo {
    margin-bottom: 0.5rem;
  }

  .login-form {
    gap: 0.875rem;
  }

  .sign-in-btn {
    margin-top: 0.5rem;
  }

  .login-footer {
    margin-top: 1rem;
  }

  .toast-container {
    top: 10px;
    right: 10px;
    max-width: 350px;
  }

  .toast-message {
    min-width: 280px;
    max-width: 350px;
  }
}

@media (max-width: 480px) {
  .form-section {
    padding: 1.25rem 1rem;
  }

  .login-header {
    margin-bottom: 1rem;
  }

  .logo {
    margin-bottom: 0.375rem;
  }

  .login-title {
    font-size: 1.3rem;
  }

  .login-subtitle {
    font-size: 0.8rem;
  }

  .login-form {
    gap: 0.75rem;
  }

  .form-input {
    padding: 0.625rem 0.75rem;
    font-size: 0.9rem;
  }

  .sign-in-btn {
    padding: 0.75rem;
    font-size: 0.95rem;
    margin-top: 0.375rem;
  }

  .form-options {
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .login-footer {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
  }

  .toast-container {
    top: 5px;
    right: 5px;
    left: 5px;
    max-width: calc(100% - 10px);
  }

  .toast-message {
    min-width: auto;
    max-width: 100%;
  }
}
</style>